import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { memberPhotoURL } from '@/api/members';
import { derive } from '@/lib/teamColor';
import { cn } from '@/lib/utils';

// MemberAvatar renders a small round bubble for a member: their profile
// photo when one's been uploaded (hasPhoto=true), otherwise a 1-2-letter
// initials fallback on a deterministic color derived from their name.
//
// The photo URL is auth-gated (member photos are PII per CLAUDE.md so
// we never expose the storage key directly) — and a plain <img src>
// can't send the Authorization: Bearer header. So we do what
// MemberCard's AuthedAvatar does: fetch() the bytes with the token,
// turn the blob into an Object URL, render that via <img>. Cleaned up
// on unmount via URL.revokeObjectURL so the browser doesn't leak.
//
// Fallback to initials when:
//   - hasPhoto=false (no photo set on the member)
//   - the fetch fails (4xx/5xx — usually 404 if the photo was
//     deleted between list-fetch and render)
//   - we're not authenticated yet (token=null at first render)
export function MemberAvatar({
  slug,
  memberID,
  firstName,
  lastName,
  hasPhoto,
  size = 'sm',
  className,
}: {
  slug: string;
  memberID: string;
  firstName: string;
  lastName: string;
  hasPhoto: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!hasPhoto || !token) {
      setSrc(null);
      return undefined;
    }
    let cancelled = false;
    let objectURL: string | null = null;
    void fetch(memberPhotoURL(slug, memberID), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`photo ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectURL = URL.createObjectURL(blob);
        setSrc(objectURL);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [slug, memberID, hasPhoto, token]);

  const initials = computeInitials(firstName, lastName);
  const fullName = `${firstName} ${lastName}`.trim() || '?';
  const palette = derive(fullName);

  const sizeClass =
    size === 'lg' ? 'h-10 w-10 text-sm' : size === 'md' ? 'h-8 w-8 text-xs' : 'h-7 w-7 text-[0.65rem]';

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-full font-heading font-semibold uppercase',
        sizeClass,
        className,
      )}
      style={
        src
          ? undefined
          : { backgroundColor: palette.bg, color: 'var(--ink)' }
      }
      aria-hidden
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="leading-none tracking-tight">{initials}</span>
      )}
    </div>
  );
}

// computeInitials picks 1-2 letters from a name. Prefer first-letter of
// each of first / last; fall back to first two letters of whichever
// name is non-empty; '?' as a last resort.
function computeInitials(first: string, last: string): string {
  const f = firstNonSpaceChar(first);
  const l = firstNonSpaceChar(last);
  if (f && l) return (f + l).toUpperCase();
  const single = (first || last).trim();
  if (single.length >= 2) return single.slice(0, 2).toUpperCase();
  if (single.length === 1) return single.toUpperCase();
  return '?';
}

function firstNonSpaceChar(s: string): string {
  const trimmed = s.trim();
  return trimmed.charAt(0);
}
